/**
 * `editorFonts` — the curated, user-selectable editor-content font catalogue
 * (2026-06-12, Chris: "The font used in the actual editor content should be user
 * selectable in settings … 30 percent tech fonts, 30 percent business, 40 percent other").
 *
 * The selection applies ONLY to the note-reading/writing surface (`--font-editor`,
 * consumed by `.rich-text-pane__content` + `.cm-aletheia-preview-content`). The rest of
 * the type system is fixed: Major Mono Display (identity), IBM Plex Sans (chrome + the
 * editor DEFAULT since remaster U2), IBM Plex Mono (data). Play was retired in U2 —
 * a persisted 'play' choice degrades to the default via loadSavedEditorFont().
 *
 * Per-device preference, same pattern as the UI scale: persisted in localStorage,
 * applied as a pure-DOM custom property, and applied at boot from `main.tsx` so the first
 * paint already uses the chosen face. Every face is self-hosted (brand-fonts.css) — nothing
 * is fetched from a CDN at runtime (local-first).
 */

export type EditorFontCategory = 'tech' | 'business' | 'editorial';

export interface EditorFont {
  /** Stable id persisted to localStorage. */
  id: string;
  /** Picker label. */
  label: string;
  /** One-line picker hint. */
  hint: string;
  category: EditorFontCategory;
  /** The CSS font-family stack written to `--font-editor`. */
  stack: string;
  /**
   * Informational only: the Google Fonts css2 `family=` parameter this face corresponds to.
   * No longer used at runtime — every face is self-hosted (brand-fonts.css) since 2026-07-02.
   */
  googleParam: string | null;
}

/** The curated nine — 2 tech / 3 business / 4 other (editorial, reading, accessible).
 *  (Was ten: Play was retired in remaster U2 with the app-wide body-voice cut.) */
export const EDITOR_FONTS: readonly EditorFont[] = [
  // ── Tech ───────────────────────────────────────────────────────────────────
  {
    id: 'space-grotesk',
    label: 'Space Grotesk',
    hint: 'Geometric techy grotesque with quirky terminals',
    category: 'tech',
    stack: "'Space Grotesk', 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    googleParam: 'Space+Grotesk:wght@400;500;700',
  },
  {
    id: 'jetbrains-mono',
    label: 'JetBrains Mono',
    hint: 'Full monospace prose — the terminal purist option',
    category: 'tech',
    stack: "'JetBrains Mono', 'Source Code Pro', ui-monospace, monospace",
    googleParam: 'JetBrains+Mono:ital,wght@0,400;0,700;1,400',
  },
  // ── Business ───────────────────────────────────────────────────────────────
  {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    hint: 'Clean corporate-engineering sans — the Aletheia default, matches the chrome',
    category: 'business',
    stack: "'IBM Plex Sans', 'Source Sans 3', ui-sans-serif, system-ui, sans-serif",
    googleParam: null, // loaded by index.html (the chrome voice)
  },
  {
    id: 'inter',
    label: 'Inter',
    hint: 'The modern product-UI standard — neutral and crisp',
    category: 'business',
    stack: "'Inter', ui-sans-serif, system-ui, sans-serif",
    googleParam: 'Inter:ital,wght@0,400;0,500;0,700;1,400',
  },
  {
    id: 'lato',
    label: 'Lato',
    hint: 'Warm professional sans — the classic business document face',
    category: 'business',
    stack: "'Lato', ui-sans-serif, system-ui, sans-serif",
    googleParam: 'Lato:ital,wght@0,400;0,700;1,400',
  },
  // ── Other (editorial, reading, accessible) ─────────────────────────────────
  {
    id: 'source-serif',
    label: 'Source Serif 4',
    hint: 'Literary serif — long-form reading comfort',
    category: 'editorial',
    stack: "'Source Serif 4', Georgia, 'Times New Roman', serif",
    googleParam: null, // self-hosted via fonts.css (400/700)
  },
  {
    id: 'lora',
    label: 'Lora',
    hint: 'Contemporary bookish serif with calligraphic roots',
    category: 'editorial',
    stack: "'Lora', Georgia, serif",
    googleParam: 'Lora:ital,wght@0,400;0,700;1,400',
  },
  {
    id: 'atkinson',
    label: 'Atkinson Hyperlegible',
    hint: 'Designed for maximum legibility (Braille Institute)',
    category: 'editorial',
    stack: "'Atkinson Hyperlegible', ui-sans-serif, system-ui, sans-serif",
    googleParam: 'Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400',
  },
  {
    id: 'nunito',
    label: 'Nunito',
    hint: 'Rounded, friendly sans — soft and easygoing',
    category: 'editorial',
    stack: "'Nunito', ui-sans-serif, system-ui, sans-serif",
    googleParam: 'Nunito:ital,wght@0,400;0,700;1,400',
  },
];

export const DEFAULT_EDITOR_FONT_ID = 'ibm-plex-sans';

const STORAGE_KEY = 'aletheia.editor_font';

export function getEditorFont(id: string): EditorFont {
  return (
    EDITOR_FONTS.find((f) => f.id === id) ??
    EDITOR_FONTS.find((f) => f.id === DEFAULT_EDITOR_FONT_ID) ??
    EDITOR_FONTS[0]
  );
}

/** Read the persisted choice; unknown/garbage values degrade to the default. */
export function loadSavedEditorFont(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null && EDITOR_FONTS.some((f) => f.id === raw)) return raw;
  } catch {
    /* localStorage unavailable — default */
  }
  return DEFAULT_EDITOR_FONT_ID;
}

/**
 * Apply a font choice: set `--font-editor` on :root + persist. Pure DOM (no React) — called
 * from the Settings picker AND from `main.tsx` at boot. No font loading here: every catalogue
 * face is self-hosted (brand-fonts.css), so the family is already available.
 */
export function applyEditorFont(id: string): void {
  const font = getEditorFont(id);
  document.documentElement.style.setProperty('--font-editor', font.stack);
  try {
    localStorage.setItem(STORAGE_KEY, font.id);
  } catch {
    /* applies in-session, no persist */
  }
}

/**
 * Kept as a stable seam for the Settings picker. No-op since 2026-07-02: every catalogue face
 * is self-hosted (brand-fonts.css), so each picker option renders its true preview immediately
 * with nothing to fetch.
 */
export function ensureAllEditorFontsLoaded(): void {
  /* all faces bundled — no runtime font fetch */
}
